import React, { useMemo } from 'react';
import { DefaultTheme, Div, Text, Loader } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';

import CONSTANTS from 'src/store/constants';
import { Card } from 'src/components';
import { formatDate, formatNumber } from 'src/functions';
import useSegment from 'src/hooks/swr/useSegment';

/* eslint-disable react/prop-types */

function SegmentCard({
  datum,
  index,
  selectedSegmentIds,
  selectedClusterIds,
  selectGridRow,
  deselectGridRow,
  onThemeChange,
}) {
  const { data: segment } = useSegment({
    segmentId: datum.segmentId || datum.id,
    options: {
      headers: {
        'X-Fields': 'pxScores,clusters{pxScores}',
      },
    },
  });

  const data = useMemo(() => {
    if (segment) {
      const clusterIndex = index.toString().split('-')[1];
      const { pxScores } = segment.clusters[clusterIndex] || segment;
      return {
        ...datum,
        pxScores,
      };
    }
    return null;
  }, [datum, index, segment]);

  const graphic = useMemo(() => {
    if (data) {
      const segments = [];
      const pxScores = data.pxScores.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted');
      const traits = pxScores.map(({ trait }) => trait);
      for (let i = 0; i < traits.length; i += 1) {
        segments.push({
          trait: traits[i],
          score: pxScores[i].score,
          order: Math.abs(50 - pxScores[i].score),
        });
      }
      segments.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);
      const top7Labels = segments.map(({ trait }) => trait).slice(0, 7);
      const top7Values = segments.map(({ score }) => score).slice(0, 7);
      return {
        top7Labels,
        top7Values,
      };
    }
    return null;
  }, [data]);

  const isSelected = useMemo(
    () =>
      data
        ? selectedSegmentIds.filter(({ segmentId }) => segmentId === data.id).length ||
          selectedClusterIds.filter(({ clusterId }) => clusterId === data.id).length
        : null,
    [data, selectedSegmentIds, selectedClusterIds]
  );

  if (!data) {
    return (
      <Card
        titleTextProps={{ p: { y: '1.58rem' } }}
        cardProps={{
          rounded: 'md',
          m: { b: '1.25rem' },
        }}
      >
        <Div flexDir="column" d="flex" justify="center" align="center" p={{ y: '10rem' }}>
          <Loader />
        </Div>
      </Card>
    );
  }

  return (
    <Card
      title={data.name}
      iconProps={{
        name: data.filter.startsWith('CLUSTERING') ? 'Clusters' : 'Segment',
        transform: 'scale(0.9)',
      }}
      titleTextProps={{
        w: '100%',
        style: {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
      }}
      cardProps={{
        rounded: 'md',
        m: { b: '1.25rem' },
      }}
      hasSelection={data.pxScores && data.pxScores.length > 0 && data.matched >= CONSTANTS.MIN_SEGMENT_MATCHES}
      isSelected={isSelected}
      onSelect={() => {
        selectGridRow({
          id: index.toString(),
          original: { ...data },
        });
      }}
      onDeselect={() => {
        deselectGridRow({
          id: index.toString(),
          original: { ...data },
        });
      }}
    >
      <Div d="flex" justify="space-between">
        <Div d="flex" flexDir="column" p={{ y: '0.5rem', x: '0.5rem' }} align="center">
          <Text textColor="tabSelected" textSize="small" m={{ b: '0.3rem' }}>
            Records
          </Text>
          <Text textSize="caption">{data.users ? formatNumber(data.users) : <>&ndash;</>}</Text>
        </Div>
        <Div d="flex" flexDir="column" p={{ y: '0.5rem', x: '0.5rem' }}>
          <Text textColor="tabSelected" textSize="small" m={{ b: '0.3rem' }}>
            Matched
          </Text>
          <Text textSize="caption">{data.matched ? formatNumber(data.matched) : <>&ndash;</>}</Text>
        </Div>
        <Div d="flex" flexDir="column" p={{ y: '0.5rem', x: '0.5rem' }}>
          <Text textColor="tabSelected" textSize="small" m={{ b: '0.3rem' }}>
            Matched %
          </Text>
          <Text textSize="caption">
            {data.users && data.matched && data.filter && !data.filter.startsWith('CLUSTERING') ? (
              `${((data.matched * 100) / data.users).toFixed(1)} %`
            ) : (
              <>&ndash;</>
            )}
          </Text>
        </Div>
        <Div d="flex" flexDir="column" p={{ y: '0.5rem', x: '0.5rem' }}>
          <Text textColor="tabSelected" textSize="small" m={{ b: '0.3rem' }}>
            Onboarded
          </Text>
          <Text textSize="caption">
            {data.created_at ? formatDate(new Date(`${data.created_at}Z`), 'mmm dd, yyyy') : <>&ndash;</>}
          </Text>
        </Div>
      </Div>
      <Div h="24rem">
        {data.pxScores && data.pxScores.length > 0 && data.matched > CONSTANTS.MIN_SEGMENT_MATCHES ? (
          <charts.Radar
            title=""
            labels={graphic.top7Labels}
            series={[
              {
                name: data.name,
                values: graphic.top7Values,
                label: { textStyle: { fontSize: 12 } },
              },
            ]}
            options={{
              backgroundColor: DefaultTheme.colors.white,
              radar: {
                radius: '68%',
                name: {
                  textStyle: {
                    fontSize: 14,
                  },
                },
              },
              series: {
                label: {
                  textStyle: {
                    fontSize: 12,
                  },
                },
              },
            }}
            showRangeControl
            relative
            theme={data.theme}
            onThemeChange={(theme) => {
              onThemeChange(theme);
            }}
          />
        ) : (
          <Div d="flex" h="100%" justify="center">
            <Div d="flex" flexDir="Column" p={{ y: '0.5rem', x: '0.5rem' }} align="space-around" alignSelf="center">
              <Text textColor="tabSelected" textSize="display1">
                Plot Unavailable
              </Text>
            </Div>
          </Div>
        )}
      </Div>
    </Card>
  );
}

export default SegmentCard;
